/**
 * Schema Generator
 *
 * Generates JSON-LD structured data for SEO (LocalBusiness, BreadcrumbList, FAQPage).
 * Reads from data-hs-replace elements and data-hs-schema markers.
 *
 * Features:
 * - LocalBusiness schema with company info, address, geo, social links
 * - Optional services and areas served (only if elements exist)
 * - BreadcrumbList schema (auto-generated from URL path)
 * - FAQPage schema (from accordion elements with data-hs-accordion-type="faq")
 * - Filters out empty/placeholder values
 * - Injects as <script type="application/ld+json"> in <head>
 *
 * Structure:
 *   <!-- Business info (uses replace data) -->
 *   <span data-hs-replace="company">Business Name</span>
 *   <span data-hs-replace="phone">(555) 123-4567</span>
 *
 *   <!-- Services wrapper -->
 *   <div data-hs-schema="services">
 *     <div data-hs-schema-list="item">Service 1</div>
 *     <div data-hs-schema-list="item">Service 2</div>
 *   </div>
 *
 *   <!-- Areas wrapper -->
 *   <div data-hs-schema="areas">
 *     <div data-hs-schema-list="item">City 1</div>
 *   </div>
 *
 *   <!-- Breadcrumb marker -->
 *   <span data-hs-schema="breadcrumb">Page Title</span>
 *
 *   <!-- FAQ accordions -->
 *   <div data-hs-accordion="wrapper">
 *     <div data-hs-accordion-type="faq">
 *       <div data-hs-accordion="toggle">Question?</div>
 *       <div data-hs-accordion="content">Answer</div>
 *     </div>
 *   </div>
 */

export function init() {
  function setupSchema() {
    const baseURL = window.location.origin;

    // Helper to get text content from data-hs-replace element
    const getReplaceText = (name: string): string => {
      const element = document.querySelector(`[data-hs-replace="${name}"]`);
      return element?.textContent?.trim() || '';
    };

    // Helper to get href from data-hs-replace element
    const getReplaceHref = (name: string): string => {
      const element = document.querySelector(`[data-hs-replace="${name}"]`);
      return (element as HTMLAnchorElement)?.href || '';
    };

    // 1. Build LocalBusiness Schema
    const businessName = getReplaceText('company');
    const phone = getReplaceText('phone');
    const email = getReplaceText('email');
    const city = getReplaceText('city');
    const state = getReplaceText('state');
    const zipCode = getReplaceText('zipcode');

    const schema: any = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: businessName,
      url: baseURL,
      telephone: phone,
      email: email,
    };

    // Add logo if it exists
    const logoElement = document.querySelector('[data-hs-replace="business-logo"] img');
    if (logoElement && (logoElement as HTMLImageElement).src) {
      schema.image = (logoElement as HTMLImageElement).src;
    }

    // Add address
    const address: any = {
      '@type': 'PostalAddress',
      addressLocality: city,
      addressRegion: state,
      postalCode: zipCode,
      addressCountry: 'US',
    };

    const streetAddress = getReplaceText('street-address');
    if (streetAddress) {
      address.streetAddress = streetAddress;
    }

    schema.address = address;

    // Add geo coordinates if they exist
    const latitude = getReplaceText('business-latitude');
    const longitude = getReplaceText('business-longitude');
    if (latitude && longitude) {
      schema.geo = {
        '@type': 'GeoCoordinates',
        latitude: latitude,
        longitude: longitude,
      };
    }

    // Add social links (filter out empty/placeholder)
    const socialNames = [
      'facebook',
      'instagram',
      'linkedin',
      'pinterest',
      'tiktok',
      'x',
      'youtube',
    ];
    const socialLinks = socialNames
      .map((name) => getReplaceHref(name))
      .filter((link) => link && link.trim() !== '' && !link.includes('placeholder'));

    if (socialLinks.length > 0) {
      schema.sameAs = socialLinks;
    }

    // Add services if wrapper exists
    const servicesWrapper = document.querySelector('[data-hs-schema="services"]');
    if (servicesWrapper) {
      const serviceItems = servicesWrapper.querySelectorAll('[data-hs-schema-list="item"]');
      const services = Array.from(serviceItems)
        .map((el) => el.textContent?.trim())
        .filter((service) => service && service !== '');

      if (services.length > 0) {
        schema.serviceType = services;
      }
    }

    // Add areas served if wrapper exists
    const areasWrapper = document.querySelector('[data-hs-schema="areas"]');
    if (areasWrapper) {
      const areaItems = areasWrapper.querySelectorAll('[data-hs-schema-list="item"]');
      const areas = Array.from(areaItems)
        .map((el) => ({
          '@type': 'City',
          name: el.textContent?.trim() || '',
        }))
        .filter((area) => area.name && area.name !== '');

      if (areas.length > 0) {
        schema.areaServed = areas;
      }
    }

    // Inject LocalBusiness schema
    const businessScript = document.createElement('script');
    businessScript.type = 'application/ld+json';
    businessScript.text = JSON.stringify(schema);
    document.head.appendChild(businessScript);

    // 2. Build BreadcrumbList Schema (if page has path)
    const breadcrumbText =
      document.querySelector('[data-hs-schema="breadcrumb"]')?.textContent?.trim() || '';
    const pathname = window.location.pathname;
    const pathParts = pathname.split('/').filter((part) => part !== '');

    if (pathParts.length > 0) {
      const breadcrumbList: any = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: baseURL,
          },
        ],
      };

      // Add middle breadcrumb (first slug capitalized)
      const firstSlug = pathParts[0];
      const middleBreadcrumb = firstSlug
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      breadcrumbList.itemListElement.push({
        '@type': 'ListItem',
        position: 2,
        name: middleBreadcrumb,
        item: baseURL + '/' + firstSlug,
      });

      // Add final breadcrumb if text exists
      if (breadcrumbText) {
        breadcrumbList.itemListElement.push({
          '@type': 'ListItem',
          position: 3,
          name: breadcrumbText,
          item: window.location.href,
        });
      }

      // Inject breadcrumb schema
      const breadcrumbScript = document.createElement('script');
      breadcrumbScript.type = 'application/ld+json';
      breadcrumbScript.text = JSON.stringify(breadcrumbList);
      document.head.appendChild(breadcrumbScript);
    }

    // 3. Build FAQPage Schema (if FAQ accordions exist)
    const hasFAQs = document.querySelector('[data-hs-accordion-type="faq"]');
    if (hasFAQs) {
      const faqArray: any[] = [];
      const faqLists = document.querySelectorAll('[data-hs-accordion="list"]');

      faqLists.forEach((faqList) => {
        const faqAccordions = faqList.querySelectorAll('[data-hs-accordion-type="faq"]');

        faqAccordions.forEach((faqAccordion) => {
          const toggleElement = faqAccordion.querySelector('[data-hs-accordion="toggle"]');
          const contentElement = faqAccordion.querySelector('[data-hs-accordion="content"]');

          if (toggleElement && contentElement) {
            const questionText = toggleElement.textContent?.trim();
            const answerHTML = contentElement.innerHTML.trim();

            faqArray.push({
              '@type': 'Question',
              name: questionText,
              acceptedAnswer: {
                '@type': 'Answer',
                text: answerHTML,
              },
            });
          }
        });
      });

      // Only create schema if we have FAQs
      if (faqArray.length > 0) {
        const faqSchema = {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqArray,
        };

        // Inject FAQ schema
        const faqScript = document.createElement('script');
        faqScript.type = 'application/ld+json';
        faqScript.text = JSON.stringify(faqSchema);
        document.head.appendChild(faqScript);
      }
    }

    // 4. Build Service Page Schema (if service elements exist)
    const nameElement = document.querySelector('[data-hs-schema-service="name"]');
    const descElement = document.querySelector('[data-hs-schema-service="description"]');

    // Require BOTH name and description
    if (nameElement && descElement) {
      const serviceName = nameElement.textContent?.trim() || '';
      // Convert description to plain text (in case it's rich text)
      const serviceDescription = descElement.textContent?.trim() || '';

      if (serviceName && serviceDescription) {
        const serviceSchema = {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: serviceName,
          description: serviceDescription,
        };

        const serviceScript = document.createElement('script');
        serviceScript.type = 'application/ld+json';
        serviceScript.text = JSON.stringify(serviceSchema);
        document.head.appendChild(serviceScript);
      }
    } else if (nameElement || descElement) {
      console.warn('[schema] Service page schema requires both "name" and "description" elements');
    }

    // 5. Build FAQ Page Schema (QAPage - single FAQ page)
    const questionElement = document.querySelector('[data-hs-schema-faq="question"]');
    const answerElement = document.querySelector('[data-hs-schema-faq="answer"]');
    const publishedElement = document.querySelector('[data-hs-schema-faq="published"]');
    const modifiedElement = document.querySelector('[data-hs-schema-faq="modified"]');

    // Require ALL 4 fields
    if (questionElement && answerElement && publishedElement && modifiedElement) {
      // Question: convert to plain text
      const questionText = questionElement.textContent?.trim() || '';
      // Answer: keep as HTML
      const answerHTML = answerElement.innerHTML.trim();
      const publishedDate = publishedElement.textContent?.trim() || '';
      const modifiedDate = modifiedElement.textContent?.trim() || '';

      if (questionText && answerHTML && publishedDate && modifiedDate) {
        const qaPageSchema = {
          '@context': 'https://schema.org',
          '@type': 'QAPage',
          mainEntity: {
            '@type': 'Question',
            name: questionText,
            text: questionText,
            answerCount: 1,
            datePublished: publishedDate,
            dateModified: modifiedDate,
            acceptedAnswer: {
              '@type': 'Answer',
              text: answerHTML,
            },
          },
        };

        const qaPageScript = document.createElement('script');
        qaPageScript.type = 'application/ld+json';
        qaPageScript.text = JSON.stringify(qaPageSchema);
        document.head.appendChild(qaPageScript);
      }
    } else if (questionElement || answerElement || publishedElement || modifiedElement) {
      const missing: string[] = [];
      if (!questionElement) missing.push('question');
      if (!answerElement) missing.push('answer');
      if (!publishedElement) missing.push('published');
      if (!modifiedElement) missing.push('modified');
      console.warn(`[schema] FAQ page schema missing required fields: ${missing.join(', ')}`);
    }

    // 6. Build Blog Page Schema
    const blogNameElement = document.querySelector('[data-hs-schema-blog="name"]');
    const blogPublishedElement = document.querySelector('[data-hs-schema-blog="published"]');
    const blogModifiedElement = document.querySelector('[data-hs-schema-blog="modified"]');
    const summaryElement = document.querySelector('[data-hs-schema-blog="summary"]');
    const authorNameElement = document.querySelector('[data-hs-schema-blog="author-name"]');
    const authorTypeElement = document.querySelector('[data-hs-schema-blog="author-type"]');

    // Require: name, published, modified, summary, author-name, author-type
    if (
      blogNameElement &&
      blogPublishedElement &&
      blogModifiedElement &&
      summaryElement &&
      authorNameElement &&
      authorTypeElement
    ) {
      const blogName = blogNameElement.textContent?.trim() || '';
      const publishedDate = blogPublishedElement.textContent?.trim() || '';
      const modifiedDate = blogModifiedElement.textContent?.trim() || '';
      // Summary: convert to plain text
      const summaryText = summaryElement.textContent?.trim() || '';
      const authorName = authorNameElement.textContent?.trim() || '';
      const authorType = authorTypeElement.textContent?.trim() || 'Organization';

      // Optional fields
      const authorRoleElement = document.querySelector('[data-hs-schema-blog="author-role"]');
      const authorImageElement = document.querySelector(
        '[data-hs-schema-blog="author-image"]'
      ) as HTMLImageElement;

      const authorRole = authorRoleElement?.textContent?.trim() || '';
      const authorImage = authorImageElement?.src || '';

      // Get company info for publisher
      const companyName = getReplaceText('company');
      const logoElement = document.querySelector('[data-hs-replace="business-logo"] img');
      const logoURL = logoElement ? (logoElement as HTMLImageElement).src : '';

      // Build author schema
      let authorSchema: any;
      if (authorType === 'Person') {
        authorSchema = {
          '@type': 'Person',
          name: authorName,
        };
        if (authorRole) authorSchema.jobTitle = authorRole;
        if (authorImage) authorSchema.image = authorImage;
      } else {
        authorSchema = {
          '@type': 'Organization',
          name: authorName,
        };
        if (logoURL) authorSchema.logo = logoURL;
      }

      const blogSchema: any = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: blogName,
        description: summaryText,
        datePublished: publishedDate,
        dateModified: modifiedDate,
        author: authorSchema,
        publisher: {
          '@type': 'Organization',
          name: companyName || authorName,
          logo: {
            '@type': 'ImageObject',
            url: logoURL,
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': window.location.href,
        },
      };

      const blogScript = document.createElement('script');
      blogScript.type = 'application/ld+json';
      blogScript.text = JSON.stringify(blogSchema);
      document.head.appendChild(blogScript);
    } else if (
      blogNameElement ||
      blogPublishedElement ||
      blogModifiedElement ||
      summaryElement ||
      authorNameElement ||
      authorTypeElement
    ) {
      const missing: string[] = [];
      if (!blogNameElement) missing.push('name');
      if (!blogPublishedElement) missing.push('published');
      if (!blogModifiedElement) missing.push('modified');
      if (!summaryElement) missing.push('summary');
      if (!authorNameElement) missing.push('author-name');
      if (!authorTypeElement) missing.push('author-type');
      console.warn(`[schema] Blog page schema missing required fields: ${missing.join(', ')}`);
    }
  }

  setupSchema();

  return {
    result: 'schema initialized',
    destroy: () => {},
  };
}
